/**
 * Composable pour le préchargement optimisé des images 360°
 * Utilise le service ImagePreloader avec un ordre de chargement intelligent
 */

import { ref, type Ref } from 'vue'
import { ImagePreloader, type PreloadProgress } from '@/services/ImagePreloader'
import type { MaterialLayer } from '@/types'

/**
 * Options pour le composable
 */
export interface UseImagePreloader360Options {
  /** Nombre d'angles affichables (ex: 12) */
  numAngleStep: number
  /** Liste des matériaux/layers */
  materials: MaterialLayer[]
  /** Chemin du répertoire des matériaux */
  materialDirPath: string
  /** Nombre total d'images par rotation (ex: 36) */
  numAngles: number
  /** Angle initial de départ (défaut: 0) */
  initialAngle?: number
  /** Nombre de connexions parallèles (défaut: 6) */
  maxConnections?: number
}

/**
 * Item du manifest d'images
 */
export interface ImageManifestItem {
  /** URL source de l'image */
  src: string
  /** Identifiant unique */
  id: string
  /** Index de l'angle (0 à numAngleStep-1) */
  angle: number
  /** Identifiant du matériau */
  materialId: string
  /** Numéro de layer */
  layer: number
}

/**
 * Valeur de retour du composable
 */
export interface UseImagePreloader360Return {
  /** Progression du chargement (0-100) */
  loadProgress: Ref<number>
  /** Indique si le chargement est en cours */
  isLoading: Ref<boolean>
  /** Set des angles chargés */
  loadedAngles: Ref<Set<number>>
  /** Vérifie si un angle est chargé */
  isAngleLoaded: (angle: number) => boolean
  /** Démarre le préchargement */
  startPreload: () => Promise<void>
  /** Annule le préchargement */
  cancelPreload: () => void
}

/**
 * Crée l'ordre de chargement optimisé (basé sur legacy _createWaitingArry)
 * Algorithme : angles opposés en priorité, puis division binaire récursive
 * 
 * Pour 12 angles à partir de 0: [0, 6, 3, 9, 1, 7, 4, 10, 2, 8, 5, 11]
 * 
 * @param numAngles - Nombre d'angles
 * @param startAngle - Angle de départ
 * @returns Tableau des indices d'angles dans l'ordre de chargement
 */
function createOptimizedLoadOrder(numAngles: number, startAngle: number): number[] {
  const order: number[] = []
  const added = new Set<number>()
  
  // Helper pour normaliser l'angle dans [0, numAngles[
  const normalize = (angle: number): number => {
    return ((angle % numAngles) + numAngles) % numAngles
  }
  
  // Helper pour ajouter un angle unique
  const addAngle = (angle: number): void => {
    const normalized = normalize(angle)
    if (!added.has(normalized)) {
      order.push(normalized)
      added.add(normalized)
    }
  }
  
  // Division binaire récursive d'un segment
  const divideBinary = (start: number, end: number, depth: number = 0): void => {
    // Protection contre récursion infinie
    if (depth > 10) return
    
    const range = end - start
    if (range <= 1) return
    
    // Ajouter le point milieu
    const mid = start + Math.floor(range / 2)
    addAngle(mid)
    
    // Continuer la division si le segment est assez grand
    if (range > 2) {
      divideBinary(start, mid, depth + 1)
      divideBinary(mid, end, depth + 1)
    }
  }
  
  // 1. Ajouter l'angle de départ
  addAngle(startAngle)
  
  // 2. Ajouter l'angle opposé (à 180°)
  const oppositeAngle = startAngle + Math.floor(numAngles / 2)
  addAngle(oppositeAngle)
  
  // 3. Diviser récursivement les deux segments
  // Segment 1: de startAngle à oppositeAngle
  divideBinary(startAngle, oppositeAngle)
  // Segment 2: de oppositeAngle à startAngle+numAngles (revient au départ)
  divideBinary(oppositeAngle, startAngle + numAngles)
  
  return order
}

/**
 * Crée le manifest complet des images à précharger
 * 
 * @param angles - Liste des indices d'angles
 * @param materials - Liste des matériaux/layers
 * @param materialDirPath - Chemin du répertoire
 * @param numAngles - Nombre total d'images par rotation
 * @param numAngleStep - Nombre d'angles affichables
 * @returns Tableau des items du manifest
 */
function createManifest(
  angles: number[],
  materials: MaterialLayer[],
  materialDirPath: string,
  numAngles: number,
  numAngleStep: number
): ImageManifestItem[] {
  const manifest: ImageManifestItem[] = []
  const angleStep = Math.round(numAngles / numAngleStep)
  
  // Pour chaque angle dans l'ordre optimisé
  for (const angleIdx of angles) {
    // Pour chaque matériau/layer
    for (const mat of materials) {
      // Calculer l'index de l'image réelle (1-based)
      const imageIndex = angleIdx * angleStep + 1
      const paddedIndex = String(imageIndex).padStart(2, '0')
      
      // Toujours utiliser PNG pour tous les layers (y compris la base)
      // afin de supporter les images avec transparence
      const extension = 'png'
      
      // Construire le chemin
      const src = `${materialDirPath}${mat.material}/${mat.material}_${paddedIndex}.${extension}`
      
      // Identifiant unique
      const id = `${angleIdx}-${mat.material}-${mat.layer}`
      
      manifest.push({
        src,
        id,
        angle: angleIdx,
        materialId: mat.material,
        layer: mat.layer
      })
    }
  }
  
  return manifest
}

/**
 * Composable pour le préchargement optimisé des images 360°
 * 
 * Fonctionnalités :
 * - Ordre de chargement optimisé (angles opposés en priorité)
 * - Préchargement intelligent : angle courant → angles adjacents → reste
 * - Suivi de progression et des angles chargés
 * - Annulation possible
 * 
 * @param options - Options de configuration
 * @returns Interface de contrôle du préchargement
 */
export function useImagePreloader360(
  options: UseImagePreloader360Options
): UseImagePreloader360Return {
  const {
    numAngleStep,
    materials,
    materialDirPath,
    numAngles,
    initialAngle = 0,
    maxConnections = 6
  } = options
  
  // État réactif
  const loadProgress = ref<number>(0)
  const isLoading = ref<boolean>(false)
  const loadedAngles = ref<Set<number>>(new Set())
  
  // Instance du preloader
  let preloader: ImagePreloader | null = null

  // Identifiant de session pour ignorer les callbacks obsolètes
  let currentSessionId = 0
  
  /**
   * Vérifie si un angle est complètement chargé
   */
  const isAngleLoaded = (angle: number): boolean => {
    return loadedAngles.value.has(angle)
  }
  
  /**
   * Démarre le préchargement avec ordre optimisé
   */
  const startPreload = async (): Promise<void> => {
    // Si un chargement est en cours, l'annuler d'abord
    if (isLoading.value || preloader) {
      cancelPreload()
    }

    // Incrémenter l'ID de session pour invalider les callbacks précédents
    currentSessionId++
    const sessionId = currentSessionId

    // Réinitialiser l'état
    loadProgress.value = 0
    isLoading.value = true
    loadedAngles.value.clear()
    
    // Créer l'ordre de chargement optimisé
    const loadOrder = createOptimizedLoadOrder(numAngleStep, initialAngle)
    
    // Générer le manifest des images
    const manifest = createManifest(
      loadOrder,
      materials,
      materialDirPath,
      numAngles,
      numAngleStep
    )
    
    // Extraire les URLs
    const urls = manifest.map(item => item.src)
    
    // Créer une map pour retrouver rapidement les métadonnées
    const manifestMap = new Map(manifest.map(item => [item.src, item]))
    
    // Tracker les angles avec toutes leurs images chargées
    const angleLoadCount = new Map<number, number>()
    const imagesPerAngle = materials.length
    
    // Initialiser les compteurs
    for (let i = 0; i < numAngleStep; i++) {
      angleLoadCount.set(i, 0)
    }
    
    // Créer le preloader
    preloader = new ImagePreloader()
    
    try {
      // Lancer le préchargement
      await preloader.preload(urls, {
        parallel: maxConnections,
        retries: 2,
        timeout: 30000,
        onProgress: (progress: PreloadProgress) => {
          // Ignorer les callbacks d'une session obsolète
          if (sessionId !== currentSessionId) {
            return
          }

          // Mettre à jour la progression globale
          loadProgress.value = progress.percentage

          // Mettre à jour les angles chargés
          if (progress.currentUrl) {
            const item = manifestMap.get(progress.currentUrl)
            if (item) {
              const currentCount = angleLoadCount.get(item.angle) || 0
              angleLoadCount.set(item.angle, currentCount + 1)

              // Si toutes les images de cet angle sont chargées
              if (angleLoadCount.get(item.angle) === imagesPerAngle) {
                loadedAngles.value.add(item.angle)
              }
            }
          }
        },
        onComplete: (results) => {
          // Ignorer les callbacks d'une session obsolète
          if (sessionId !== currentSessionId) {
            return
          }

          console.log(`Préchargement terminé: ${results.filter(r => r.success).length}/${results.length} réussies`)
          isLoading.value = false
        },
        onError: (error) => {
          // Ignorer les callbacks d'une session obsolète
          if (sessionId !== currentSessionId) {
            return
          }

          console.error('Erreur de chargement:', error)
        }
      })
    } catch (error) {
      // Ignorer les erreurs d'une session obsolète
      if (sessionId !== currentSessionId) {
        return
      }

      console.error('Erreur lors du préchargement:', error)
      isLoading.value = false
    }
  }
  
  /**
   * Annule le préchargement en cours
   */
  const cancelPreload = (): void => {
    if (preloader) {
      preloader.cancel()
      preloader = null
      isLoading.value = false
    }
  }
  
  return {
    loadProgress,
    isLoading,
    loadedAngles,
    isAngleLoaded,
    startPreload,
    cancelPreload
  }
}